/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { Button, Col, Form, Input, InputNumber, notification, Row } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { ISetting } from '@/interfaces/setting';
import { editSettings } from '@/adapters/setting';

interface IProps {
  data: ISetting;
  onClose: () => void;
}

const CompanyMaintainEdit = ({ data, onClose }: IProps) => {
  const [form] = Form.useForm<Partial<ISetting>>();

  const mutations = useMutation({
    mutationFn: async (data: Partial<ISetting>) => editSettings(data),
    mutationKey: ['editSetting'],
    onSuccess: () => {
      notification.success({
        message: 'อัพเดทสำเร็จ',
      });
      onReset();
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<ISetting>) => {
        values.id = Number(data?.id || 0);
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
      });
    }
  }, [data, form]);

  return (
    <div className="flex w-full">
      <Form
        form={form}
        name="company-form"
        onFinish={handleUpdate}
        layout="horizontal"
        className="w-full"
        labelCol={{ flex: '250px' }}
      >
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="ชื่อบริษัท"
              name="companyName"
              rules={[{ required: true, message: 'ระบุชื่อบริษัท' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="ชื่อบริษัท (English)"
              name="companyNameEn"
              rules={[{ required: true, message: 'ระบุชื่อบริษัท (English)' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Tax ID"
              name="companyTaxId"
              rules={[
                { required: true, message: 'ระบุ Tax ID' },
                { len: 13, message: 'ระบุจำนวน 13 หลัก' },
              ]}
            >
              <Input size="large" minLength={13} maxLength={13} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="ที่อยู่"
              name="companyAddress"
              rules={[{ required: true, message: 'ระบุที่อยู่' }]}
            >
              <Input.TextArea rows={2} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="ที่อยู่ (ออกเอกสาร)"
              name="companyAddressBilling"
              rules={[{ required: true, message: 'ระบุที่อยู่ (ออกเอกสาร)' }]}
            >
              <Input.TextArea rows={2} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="ที่อยู่ (ออกเอกสาร) English"
              name="companyAddressBillingEn"
              rules={[
                { required: true, message: 'ระบุที่อยู่ (ออกเอกสาร) English' },
              ]}
            >
              <Input.TextArea rows={2} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="เบอร์โทร"
              name="companyPhoneNumber"
              rules={[{ required: true, message: 'ระบุเบอร์โทร' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="FAX"
              name="companyPhoneFax"
              rules={[{ required: true, message: 'ระบุ FAX' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Website"
              name="website"
              rules={[{ required: true, message: 'ระบุ Website' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="อีเมล์"
              name="companyEmail"
              rules={[{ required: true, message: 'ระบุอีเมล์' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="ชื่อสำหรับออก Cheuque (ไทย)"
              name="companyChequeNameTh"
              rules={[
                { required: true, message: 'ระบุชื่อสำหรับออก Cheuque (ไทย)' },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="ชื่อสำหรับออก Cheuque (อังกฤษ)"
              name="companyChequeNameEn"
              rules={[
                {
                  required: true,
                  message: 'ระบุชื่อสำหรับออก Cheuque (อังกฤษ)',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Bank Code"
              name="companyBankCode"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Bank Code',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Biller ID"
              name="companyBillerId"
              rules={[
                {
                  required: true,
                  message: 'ระบุBiller ID',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Reference Code"
              name="referenceCode"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Reference Code',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="SWIF CODE"
              name="swifCode"
              rules={[
                {
                  required: true,
                  message: 'ระบุ SWIF CODE',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Bank Name"
              name="bankName"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Bank Name',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Bank Address"
              name="bankAddress"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Bank Address',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Bank Code (Export)"
              name="branchCodeExport"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Bank Code (Export)',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Branch Code"
              name="branchCode"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Branch Code',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Branch Name"
              name="branchName"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Branch Name',
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Rate (USD)"
              name="rate"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Rate',
                },
              ]}
            >
              <InputNumber size="large" min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item<ISetting>
              label="Driver App Link"
              name="driverLink"
              rules={[
                {
                  required: true,
                  message: 'ระบุ Driver App Link',
                },
              ]}
            >
              <Input size="large" placeholder="Driver App Link" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <div className="flex gap-3 justify-end">
              <Button type="default" htmlType="button" onClick={onReset}>
                ยกเลิก
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={mutations?.isPending}
                className=""
              >
                บันทึก
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CompanyMaintainEdit;
